import htmlToPdfmake from 'html-to-pdfmake'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { I18n, Translate } from 'react-redux-i18n'
import { useSearchParams } from 'react-router-dom'
import { languages } from '../../../../../../configs/language'
import useViewport from '../../../../../common/hooks/useViewport'
import { Element } from '../../../../../common/html/element'
import logoFiinGroupEN from '../../../../../common/icons/logoFiinGroupEN.svg'
import logoFiinGroupVI from '../../../../../common/icons/logoFiinGroupVI.svg'
import { Loading } from '../../../../../common/loading'
import { getUnique } from '../../../../../helpers/common'
import { FORMAT, formatDateTime } from '../../../../../helpers/datetime'
import { BIReport, getIdsAndGetDataFromRedux } from '../orderDetails/BIReport'
import { BusinessConcierge } from '../orderDetails/BusinessConcierge'
import { FactSheet } from '../orderDetails/FactSheet'
import { InternationalReport } from '../orderDetails/InternationalReport'
import { Screening } from '../orderDetails/Screening'
import { OrderInformation } from '../orderInformation'
import {
  selectActiveReport,
  selectBIReportIds,
  selectComprehensiveIds,
  selectConciergeServiceIds,
  selectCreditIds,
  selectFactSheetIds,
  selectInternationalReportIds,
  selectScreeningIds,
  selectStandardIds,
} from '../store/slice'
import { HtmlToImage } from './HtmlToImage'
import PdfData from './PdfData'
import { PADDING_PDF, WIDTH_PDF_PORTRAIT_WITHOUT_PADDING } from './constant'
import footer from './footer.png'
import classes from './index.module.scss'

pdfMake.vfs = pdfFonts.pdfMake.vfs

const imageFooter = new Image()
imageFooter.src = footer
imageFooter.crossOrigin = 'anonymous'

const Pdf = () => {
  const [search] = useSearchParams()

  const id = search.get('orderId')

  const locale = useSelector((state) => state.i18n.locale)
  const factSheetIds = useSelector(selectFactSheetIds)
  const standardIds = useSelector(selectStandardIds)
  const creditIds = useSelector(selectCreditIds)
  const comprehensiveIds = useSelector(selectComprehensiveIds)
  const bIReportIds = useSelector(selectBIReportIds)
  const activeReport = useSelector(selectActiveReport)
  const screeingIds = useSelector(selectScreeningIds)
  const internationalIds = useSelector(selectInternationalReportIds)
  const conciergeIds = useSelector(selectConciergeServiceIds)

  const { ids: bIIds } = getIdsAndGetDataFromRedux({
    activeReport,
    creditIds,
    standardIds,
    comprehensiveIds,
    bIReportIds,
  })

  const isVi = locale === languages.vi

  const imageLogo = new Image()
  imageLogo.src = isVi ? logoFiinGroupVI : logoFiinGroupEN
  imageLogo.crossOrigin = 'anonymous'

  const pdfRef = useRef()
  const pdfData = useRef(new PdfData())
  const countDone = useRef(0)
  const items = useRef([])
  const { width, height } = useViewport()

  const [pages, setPages] = useState([])
  const [currentItem, setCurrentItem] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (pages.length) {
      downloadPdf()
    }
  }, [pages])

  const startDownload = () => {
    setDefault()
    setIsLoading(true)
    const newItems = []

    newItems.push(<OrderInformation isPdf />)

    if (!!factSheetIds.length) {
      newItems.push(<FactSheet isPdf />)
    }

    if (!!bIIds.length) {
      newItems.push(<BIReport isPdf />)
    }

    if (!!screeingIds.length) {
      newItems.push(<Screening isPdf />)
    }

    if (!!internationalIds.length) {
      newItems.push(<InternationalReport isPdf />)
    }

    if (!!conciergeIds.length) {
      newItems.push(<BusinessConcierge isPdf />)
    }

    if (newItems.length) {
      items.current = newItems
      setCurrentItem(items.current[0])
    }
  }

  const setDefault = () => {
    pdfData.current = new PdfData()
    countDone.current = 0
    items.current = []
    setPages([])
  }

  const downloadPdf = () => {
    if (pdfRef.current) {
      const htmlPdfMake = htmlToPdfmake(`${pdfRef.current.innerHTML}`, {
        imagesByReference: true,
        defaultStyles: {},
        tableAutoSize: true,
      })

      const getPdfFooter = () => {
        const c = document.createElement('canvas')
        const ctx = c.getContext('2d')
        c.width = 495 * 5
        c.height = 45 * 5
        ctx.drawImage(imageFooter, 0, 0, 495 * 5, 45 * 5)
        const footer = c.toDataURL('image/jpg')

        return {
          stack: [
            {
              image: footer,
              width: 495,
              height: 45,
              margin: [PADDING_PDF, 0, PADDING_PDF, 15],
            },
          ],
        }
      }

      const getPdfHeader = () => {
        const c = document.createElement('canvas')
        const ctx = c.getContext('2d')
        c.width = 150 * 5
        c.height = 35 * 5
        ctx.drawImage(imageLogo, 0, 0, 150 * 5, 35 * 5)
        const logo = c.toDataURL('image/png')

        return {
          stack: [
            {
              table: {
                widths: [180, isVi ? 150 : 135, isVi ? 130 : 145],
                body: [
                  [
                    {
                      image: logo,
                      width: 150,
                      height: 35,
                      margin: [0, 12, 0, 0],
                    },
                    {
                      layout: 'noBorders',
                      table: {
                        headerRows: 0,
                        widths: [isVi ? 150 : 135],
                        body: [
                          [
                            {
                              text: I18n.t(
                                'userManagement.orderManagement.orderDetail.HEAD_OFFICE',
                              ),
                              bold: true,
                              fontSize: 8,
                              color: '#1464b3',
                              margin: [0, 0, 10, 0],
                            },
                          ],
                          [
                            {
                              text: I18n.t(
                                'userManagement.orderManagement.orderDetail.HN_ADDRESS',
                              ),
                              fontSize: 8,
                              color: '#1362b0',
                              margin: [0, 0, 10, 0],
                            },
                          ],
                          [
                            {
                              text:
                                I18n.t(
                                  'userManagement.orderManagement.orderDetail.PHONE',
                                ) + '+84 24 3562 6962',
                              fontSize: 8,
                              color: '#1362b0',
                              margin: [0, 0, 10, 0],
                            },
                          ],
                        ],
                      },
                    },
                    {
                      layout: 'noBorders',
                      table: {
                        headerRows: 0,
                        widths: [isVi ? 130 : 145],
                        body: [
                          [
                            {
                              text: I18n.t(
                                'userManagement.orderManagement.orderDetail.HCM_BRANCH',
                              ),
                              bold: true,
                              fontSize: 8,
                              color: '#1464b3',
                            },
                          ],
                          [
                            {
                              text: I18n.t(
                                'userManagement.orderManagement.orderDetail.HCM_ADDRESS',
                              ),
                              fontSize: 8,
                              color: '#1362b0',
                            },
                          ],
                          [
                            {
                              text:
                                I18n.t(
                                  'userManagement.orderManagement.orderDetail.PHONE',
                                ) + '+84 28 3933 3585/ 86',
                              fontSize: 8,
                              color: '#1362b0',
                            },
                          ],
                        ],
                      },
                    },
                  ],
                ],
              },
              layout: 'noBorders',
            },
          ],
          margin: [60, 35, 60, 0],
        }
      }

      const docDefinition = {
        pageSize: 'A4',
        pageMargins: [PADDING_PDF, 110, PADDING_PDF, 60],
        header: getPdfHeader,
        footer: getPdfFooter(),
        content: [...htmlPdfMake.content],
        images: htmlPdfMake.images,
      }

      pdfMake
        .createPdf(docDefinition)
        .download(
          `FG_Confirmation_${id}_${formatDateTime(
            new Date(),
            FORMAT.FILE_DOWNLOAD,
          )}.pdf`,
        )
      setDefault()
      setIsLoading(false)
    }
  }

  const onDoneProcess = (item) => {
    pdfData.current.addContent(item)
    countDone.current += 1
    if (countDone.current === items.current.length) {
      setPages(pdfData.current.getData())
    } else {
      setCurrentItem(items.current[countDone.current])
    }
  }

  useEffect(() => {
    return () => {
      const matches = document.querySelectorAll('body > div')
      if (matches.length > 3) {
        matches.forEach((item) => {
          if (item.id !== 'root') {
            document.body.removeChild(item)
          }
        })
      }
    }
  }, [])

  return (
    <>
      <Element
        className={classes.btn}
        style={{ backgroundColor: '#0553A5', color: '#FFFFFF' }}
        onClick={startDownload}
      >
        <Translate value="userManagement.orderManagement.orderDetail.DOWNLOAD_CONFIRMATION" />
      </Element>
      {createPortal(
        <div
          style={{ display: isLoading ? 'block' : 'none', height, width }}
          className={classes.loadingContainer}
        >
          <div className="positionRelative wh100">
            <Loading />
          </div>
        </div>,
        document.body,
      )}
      <div className={classes.pdfContainer}>
        <div ref={pdfRef}>
          {pages.map(({ page, items }) => {
            return (
              <div
                style={{
                  width: WIDTH_PDF_PORTRAIT_WITHOUT_PADDING,
                }}
                data-pdfmake={
                  page !== 1 ? '{"pageBreak": "before"}' : undefined
                }
                key={page}
              >
                {items.map((item, index) => {
                  return (
                    <img
                      key={index}
                      src={item.img}
                      alt="img"
                      style={{
                        width: WIDTH_PDF_PORTRAIT_WITHOUT_PADDING,
                        marginTop: 20,
                      }}
                    />
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
      {currentItem && (
        <HtmlToImage
          key={getUnique()}
          id={'pdfItem' + countDone.current}
          onDoneProcess={onDoneProcess}
          availableHeightPage={pdfData.current.getAvailableHeightPage()}
        >
          <Element
            style={{
              width: 1000,
              fontFamily: 'sans-serif',
              color: '#3A414B',
            }}
          >
            {currentItem}
          </Element>
        </HtmlToImage>
      )}
    </>
  )
}

export default Pdf
